import React from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { UserProfile, MarkersOutsideOptimalDisplay } from '../../models/UserProfile';
import { Section } from '../../models';
import { UserProfileUIStore } from '../../stores/user-profile-ui-store';
import { LogoUpload } from '../logo/logo-upload';
import { supplementsVisible } from '../../utils/supplementsVisibility';

interface SettingsPageProps {
  userProfile: UserProfile;
  userProfileUIStore?: UserProfileUIStore;
  onLogoUploaded: (logoUrl: string) => void;
  onLogoRemoved: () => void;
  onPreparedByChange: (value: string) => void;
  onBrandsChange: (brands: string[]) => void;
  onExcludedFromPdfSectionsChange: (sections: Section[]) => void;
  onMarkersOutsideOptimalDisplayChange: (mode: MarkersOutsideOptimalDisplay) => void;
  onShowOnlySectionSignalsChange: (value: boolean) => void;
  onShowOnlyImmuneSectionSignalsChange: (value: boolean) => void;
  onShowImmuneAbsolutesChange: (value: boolean) => void;
  onComparisonMatchAnalysisOrderChange: (value: boolean) => void;
  onShowPreviousTestMarkersInComparisonChange: (value: boolean) => void;
  onSaveSettings: () => void;
  onError: (error: string) => void;
}

// The complete list of sections that appear in Test Results Analysis and can
// be toggled on/off in the PDF. Order here drives the Settings UI order.
const PDF_INCLUSION_SECTIONS: { section: Section; label: string }[] = [
  { section: Section.BMI,                           label: 'BMI' },
  { section: Section.RESULTS_OVERVIEW,              label: 'Results Overview' },
  { section: Section.MARKERS_OUTSIDE_OPTIMAL,       label: 'Markers Outside Optimal' },
  { section: Section.RBC,                           label: 'Blood Oxygen & Red Blood Cell Function' },
  { section: Section.IRON,                          label: 'Iron Status & Utilization' },
  { section: Section.WBC,                           label: 'Immune Activity' },
  { section: Section.PLATELETS,                     label: 'Blood Clotting Function' },
  { section: Section.GLUCOSE,                       label: 'Glucose Regulation & Metabolic Function' },
  { section: Section.KIDNEY,                        label: 'Kidney Function' },
  { section: Section.PROTEINS,                      label: 'Protein Status' },
  { section: Section.NUTRIENTS,                     label: 'Vitamin & Mineral Status' },
  { section: Section.ELECTROLYTES,                  label: 'Electrolyte Balance' },
  { section: Section.LIVER_BILIARY,                 label: 'Liver Function' },
  { section: Section.LIPIDS,                        label: 'Lipid Balance' },
  { section: Section.INFLAMMATION_OXIDATIVE_STRESS, label: 'Inflammation & Oxidative Stress' },
  { section: Section.THYROID,                       label: 'Thyroid Function' },
  { section: Section.STRESS_ADRENAL,                label: 'Stress & Adrenal Balance' },
  { section: Section.SEX_HORMONE,                   label: 'Hormone Balance' },
  { section: Section.PROSTATE,                      label: 'Prostate Function' },
  { section: Section.SUPPLEMENTS,                   label: 'Supplements' },
];

export const SettingsPage: React.FC<SettingsPageProps> = ({
  userProfile,
  userProfileUIStore,
  onLogoUploaded,
  onLogoRemoved,
  onPreparedByChange,
  onBrandsChange,
  onExcludedFromPdfSectionsChange,
  onMarkersOutsideOptimalDisplayChange,
  onShowOnlySectionSignalsChange,
  onShowOnlyImmuneSectionSignalsChange,
  onShowImmuneAbsolutesChange,
  onComparisonMatchAnalysisOrderChange,
  onShowPreviousTestMarkersInComparisonChange,
  onSaveSettings,
  onError,
}) => {
  const [preparedBy, setPreparedBy] = React.useState(
    userProfile.preparedBy || '',
  );

  const [enabledBrands, setEnabledBrands] = React.useState<string[]>(
    userProfile.enabledSupplementBrands || [],
  );

  const [excludedFromPdfSections, setExcludedFromPdfSections] = React.useState<Section[]>(
    userProfile.excludedFromPdfSections || [],
  );

  const [markersOutsideOptimalDisplay, setMarkersOutsideOptimalDisplay] =
    React.useState<MarkersOutsideOptimalDisplay>(
      userProfile.markersOutsideOptimalDisplay || 'showBars',
    );

  const handleMarkersOutsideOptimalDisplayChange = (mode: MarkersOutsideOptimalDisplay) => {
    setMarkersOutsideOptimalDisplay(mode);
    onMarkersOutsideOptimalDisplayChange(mode);
  };

  const [showOnlySectionSignals, setShowOnlySectionSignals] = React.useState<boolean>(
    userProfile.showOnlySectionSignals ?? true,
  );

  const handleShowOnlySectionSignalsChange = (value: boolean) => {
    setShowOnlySectionSignals(value);
    onShowOnlySectionSignalsChange(value);
  };

  const [showOnlyImmuneSectionSignals, setShowOnlyImmuneSectionSignals] = React.useState<boolean>(
    userProfile.showOnlyImmuneSectionSignals ?? true,
  );

  const handleShowOnlyImmuneSectionSignalsChange = (value: boolean) => {
    setShowOnlyImmuneSectionSignals(value);
    onShowOnlyImmuneSectionSignalsChange(value);
  };

  const [showImmuneAbsolutes, setShowImmuneAbsolutes] = React.useState<boolean>(
    userProfile.showImmuneAbsolutes ?? true,
  );

  const handleShowImmuneAbsolutesChange = (value: boolean) => {
    setShowImmuneAbsolutes(value);
    onShowImmuneAbsolutesChange(value);
  };

  const [comparisonMatchAnalysisOrder, setComparisonMatchAnalysisOrder] =
    React.useState<boolean>(userProfile.comparisonMatchAnalysisOrder ?? true);

  const handleComparisonMatchAnalysisOrderChange = (value: boolean) => {
    setComparisonMatchAnalysisOrder(value);
    onComparisonMatchAnalysisOrderChange(value);
  };

  const [
    showPreviousTestMarkersInComparison,
    setShowPreviousTestMarkersInComparison,
  ] = React.useState<boolean>(
    userProfile.showPreviousTestMarkersInComparison ?? true,
  );

  const handleShowPreviousTestMarkersInComparisonChange = (value: boolean) => {
    setShowPreviousTestMarkersInComparison(value);
    onShowPreviousTestMarkersInComparisonChange(value);
  };

  const isIncluded = (section: Section) => !excludedFromPdfSections.includes(section);

  const handleIncludeInPdfToggle = (section: Section) => {
    const next = isIncluded(section)
      ? [...excludedFromPdfSections, section]
      : excludedFromPdfSections.filter(s => s !== section);
    setExcludedFromPdfSections(next);
    onExcludedFromPdfSectionsChange(next);
  };

  const handlePreparedByChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.length > 32) {
      return;
    }
    setPreparedBy(val);
    onPreparedByChange(val);
  };

  const handleBrandToggle = (brand: string) => {
    let newBrands = [...enabledBrands];
    if (newBrands.includes(brand)) {
      newBrands = newBrands.filter(b => b !== brand);
    } else {
      newBrands.push(brand);
    }
    setEnabledBrands(newBrands);
    onBrandsChange(newBrands);
  };

  return (
    <div>
      <h5>General Settings</h5>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formPreparedBy">
          <Form.Label column sm={3}>
            Prepared By
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '240px' }}
              placeholder="e.g. MaryAnn Marks"
              value={preparedBy}
              onChange={handlePreparedByChange}
            />
            <Form.Text className="text-muted">
              When you enter a name in this field, it will appear as "Prepared
              by: NAME" on the cover page and footer of both PDF reports.
              <br />
              If you choose not to include a "Prepared by: NAME" on the reports,
              leave this field blank.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save General Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

     <h5>Custom Logo</h5>
      <p className="text-muted">
        Manage the logo that appears on your reports.
      </p>
      <hr />

      <LogoUpload
        userProfile={userProfile}
        userProfileUIStore={userProfileUIStore}
        onLogoUploaded={onLogoUploaded}
        onLogoRemoved={onLogoRemoved}
        onError={onError}
      />

      <h5 className="mt-4">PDF Inclusion</h5>
      <p className="text-muted">
        Sets the default state of the &quot;Include in PDF&quot; checkbox in each
        section&apos;s header on the Test Results Analysis page. Unchecking a
        section here means it stays out of the PDF on every test by default. You
        can still flip the checkbox per-test to include or exclude that section
        for a specific export. These settings do not affect on-screen viewing.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formPdfInclusion">
          <Form.Label column sm={3}>
            Default sections to include
          </Form.Label>
          <Col sm={9}>
            {PDF_INCLUSION_SECTIONS
              .filter(({ section }) =>
                // Supplements summary panel is hidden in prod, so don't show a
                // setting toggle that has no effect there.
                section !== Section.SUPPLEMENTS || supplementsVisible(),
              )
              .map(({ section, label }) => (
                <Form.Check
                  key={section}
                  type="checkbox"
                  id={`pdf-include-${section}`}
                  label={label}
                  checked={isIncluded(section)}
                  onChange={() => handleIncludeInPdfToggle(section)}
                />
              ))}
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save PDF Inclusion Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Markers Outside Optimal</h5>
      <p className="text-muted">
        Controls how the &quot;Markers Outside Optimal&quot; section appears on the
        Test Results Analysis page.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formMarkersOutsideOptimal">
          <Form.Label column sm={3}>
            Display mode
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="radio"
              id="markers-outside-optimal-show-bars"
              name="markersOutsideOptimalDisplay"
              label="Show bar graphs (default)"
              checked={markersOutsideOptimalDisplay === 'showBars'}
              onChange={() => handleMarkersOutsideOptimalDisplayChange('showBars')}
            />
            <Form.Check
              type="radio"
              id="markers-outside-optimal-hide-bars"
              name="markersOutsideOptimalDisplay"
              label="Hide bar graphs"
              checked={markersOutsideOptimalDisplay === 'hideBars'}
              onChange={() => handleMarkersOutsideOptimalDisplayChange('hideBars')}
            />
            <Form.Check
              type="radio"
              id="markers-outside-optimal-hide-section"
              name="markersOutsideOptimalDisplay"
              label="Hide section"
              checked={markersOutsideOptimalDisplay === 'hideSection'}
              onChange={() => handleMarkersOutsideOptimalDisplayChange('hideSection')}
            />
            <Form.Check
              type="radio"
              id="markers-outside-optimal-minimize-section"
              name="markersOutsideOptimalDisplay"
              label="Minimize section"
              checked={markersOutsideOptimalDisplay === 'minimizeSection'}
              onChange={() => handleMarkersOutsideOptimalDisplayChange('minimizeSection')}
            />
            <Form.Text className="text-muted">
              &quot;Show bar graphs&quot; opens the section with reference-range
              bars visible in each panel. &quot;Hide bar graphs&quot; opens the
              section with each panel collapsed — only the marker name, value, and
              HIGH/LOW badge are shown until you click the panel to expand it.
              &quot;Hide section&quot; removes the entire section from the page.
              &quot;Minimize section&quot; collapses the whole section to its
              header — click the header to open it. Only &quot;Hide section&quot;
              affects the PDF; a minimized section still exports in full.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Markers Outside Optimal Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Inflammation Section</h5>
      <p className="text-muted">
        Controls which markers appear in the Inflammation &amp; Oxidative
        Stress section on the Test Results Analysis page.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formShowOnlySectionSignals">
          <Form.Label column sm={3}>
            Marker visibility
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-only-section-signals"
              label="Only show markers with a section signal"
              checked={showOnlySectionSignals}
              onChange={() => handleShowOnlySectionSignalsChange(!showOnlySectionSignals)}
            />
            <Form.Text className="text-muted">
              When checked (default), cross-section markers (e.g. ferritin,
              GGT, albumin, HDL) only appear in the Inflammation section
              when their value contributes a signal there — markers that
              are optimal or out of optimal in the opposite direction
              (e.g. high albumin, low ferritin) are hidden, since they have
              a home in another section. Markers whose only home is this
              section (hs-CRP, ESR, homocysteine) always remain visible.
              If filtering empties the section, it disappears entirely.
              Uncheck to show every marker assigned to the section.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Inflammation Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Immune Section</h5>
      <p className="text-muted">
        Controls which markers appear in the Immune Activity section on the
        Test Results Analysis page.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formShowOnlyImmuneSectionSignals">
          <Form.Label column sm={3}>
            Marker visibility
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-only-immune-section-signals"
              label="Only show markers with a section signal"
              checked={showOnlyImmuneSectionSignals}
              onChange={() => handleShowOnlyImmuneSectionSignalsChange(!showOnlyImmuneSectionSignals)}
            />
            <Form.Text className="text-muted">
              When checked (default), cross-section markers only appear in
              the Immune section when their value contributes a signal
              there. For example, alkaline phosphatase is hidden when
              optimal or high — only a LOW Alk Phos signals low zinc,
              which the immune system needs. Globulin is hidden when
              optimal. Markers whose only home is this section (WBC and
              the differentials) always remain visible. If filtering
              empties the section, it disappears entirely. Uncheck to show
              every marker assigned to the section.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="formShowImmuneAbsolutes">
          <Form.Label column sm={3}>
            Absolute differentials
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-immune-absolutes"
              label="Show absolutes"
              checked={showImmuneAbsolutes}
              onChange={() => handleShowImmuneAbsolutesChange(!showImmuneAbsolutes)}
            />
            <Form.Text className="text-muted">
              When checked (default), the absolute differential bar graphs
              (Absolute Neutrophils, Lymphocytes, Monocytes, Eosinophils,
              Basophils) are shown in the Immune Activity section. Uncheck to
              hide those bar graphs. The percentage differentials always
              remain visible.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Immune Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Blood Work Comparison Section Order</h5>
      <p className="text-muted">
        Controls the default order of sections in the Blood Work Comparison
        table and its PDF export.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formComparisonMatchAnalysisOrder">
          <Form.Label column sm={3}>
            Section order
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="comparison-match-analysis-order"
              label="Match Analysis Order"
              checked={comparisonMatchAnalysisOrder}
              onChange={() =>
                handleComparisonMatchAnalysisOrderChange(
                  !comparisonMatchAnalysisOrder,
                )
              }
            />
            <Form.Text className="text-muted">
              When checked (default), the comparison table and PDF order their
              sections to match the Test Results Analysis page. Uncheck to use
              the legacy lab-report order (CBC markers, then CMP, then lipids,
              etc.). This sets the default; the comparison view also has a
              "Match Analysis Order" checkbox to change it on the fly.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Comparison Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Test Comparison Bar Graphs</h5>
      <p className="text-muted">
        Controls which markers get a reference-range bar graph on the Test
        Results Analysis page and PDF when a report compares the current test
        to a previous one.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formShowPreviousTestMarkers">
          <Form.Label column sm={3}>
            Previous-test markers
          </Form.Label>
          <Col sm={9}>
            <Form.Check
              type="checkbox"
              id="show-previous-test-markers"
              label="Show markers only in the previous test"
              checked={showPreviousTestMarkersInComparison}
              onChange={() =>
                handleShowPreviousTestMarkersInComparisonChange(
                  !showPreviousTestMarkersInComparison,
                )
              }
            />
            <Form.Text className="text-muted">
              When checked (default), a comparison report also shows a bar graph
              for each marker that has a result in the previous test but not the
              current one, plotting just the previous (blue) blood drop and
              tagging the row &quot;prev only&quot;. This gives a complete
              side-by-side even when the current test dropped a marker — for
              example, when a section improved to a thumbs-up because markers
              that were out of range last time are no longer being measured.
              Uncheck to show only markers present in the current test. This has
              no effect when viewing a single test.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={onSaveSettings}>
              Save Test Comparison Settings
            </Button>
          </Col>
        </Form.Group>
      </Form>

      {supplementsVisible() && (
        <>
          <h5>Supplement Settings</h5>
          <hr />

          <Form className="mb-5">
            <Form.Group as={Row} controlId="formBrands">
              <Form.Label column sm={3}>
                Enabled Brands
              </Form.Label>
              <Col sm={9}>
                <Form.Check
                  type="checkbox"
                  id="brand-systemic-formulas"
                  label="Systemic Formulas (Use code LABSMARTS10 to get 10% off your first order.)"
                  checked={enabledBrands.includes('systemicFormulas')}
                  onChange={() => handleBrandToggle('systemicFormulas')}
                />
                <Form.Check
                  type="checkbox"
                  id="brand-bwh-labs"
                  label="BWH Labs (Use code LABSMARTS10 to get 10% off your first order.)"
                  checked={enabledBrands.includes('bwhLabs')}
                  onChange={() => handleBrandToggle('bwhLabs')}
                />
                <Form.Text className="text-muted">
                  Select which supplement brands to display in the Recommendations
                  tab. Results are updated automatically when you save.
                </Form.Text>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Col sm={{ span: 9, offset: 3 }}>
                <Button variant="primary" onClick={onSaveSettings}>
                  Save Supplement Settings
                </Button>
              </Col>
            </Form.Group>
          </Form>
        </>
      )}
    </div>
  );
};
